import React, { useState, useEffect, useRef } from 'react';
import API_CONFIG from '../../apiConfig';
import CustomSelect from '../ui/CustomSelect';

interface PoseSelectionProps {
    selectedPoseIds: string[];
    setSelectedPoseIds: React.Dispatch<React.SetStateAction<string[]>>;
    onPoseSelect?: (poseUrl: string, poseId: string) => void;
    disabled?: boolean;
    genderFilter?: string;
    poseTypeFilter?: string;
    sizeFilter?: string;
    onGenderFilterChange?: (gender: string) => void;
    onPoseTypeFilterChange?: (poseType: string) => void;
    onSizeFilterChange?: (size: string) => void;
}

interface Pose {
    poseId: string;
    imageUrl: string[];
    prompt: string;
    type: string;
    timestamp: string;
    gender: string;
    generationType: string;
    poseName: string;
    liveStatus: boolean;
    size?: string;
}

const PoseSelection: React.FC<PoseSelectionProps> = ({
    selectedPoseIds,
    setSelectedPoseIds,
    onPoseSelect,
    disabled = false,
    genderFilter = 'female',
    poseTypeFilter = 'standing',
    sizeFilter = 'normal',
    onGenderFilterChange,
    onPoseTypeFilterChange,
    onSizeFilterChange
}) => {
    const [poses, setPoses] = useState<Pose[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const fetchPoses = async () => {
            try {
                setLoading(true);
                const response = await fetch(API_CONFIG.GET_POSE_PRESETS_URL);

                // Check HTTP status
                if (!response.ok) {
                    setError('Something went wrong. Please try again.');
                    return;
                }

                const data = await response.json();

                // Check if poses were returned
                if (data.success && data.poses && data.poses.length > 0) {
                    // Filter poses to only show those with liveStatus: true
                    const livePoses = data.poses.filter((pose: Pose) => pose.liveStatus === true);

                    // Sort poses by poseId for consistent order
                    const sortedPoses = livePoses.sort((a: Pose, b: Pose) => {
                        const poseIdA = a.poseId.toUpperCase();
                        const poseIdB = b.poseId.toUpperCase();

                        // Extract prefix and number from poseId (e.g., "F1" -> ["F", "1"], "PF10" -> ["PF", "10"])
                        const extractParts = (id: string) => {
                            const match = id.match(/^([A-Z]+)(\d+)$/);
                            if (match) {
                                return { prefix: match[1], number: parseInt(match[2], 10) };
                            }
                            return { prefix: id, number: 0 };
                        };

                        const partsA = extractParts(poseIdA);
                        const partsB = extractParts(poseIdB);

                        // First sort by prefix (F, M, PF)
                        const prefixOrder: { [key: string]: number } = { 'F': 1, 'M': 2, 'PF': 3 };
                        const orderA = prefixOrder[partsA.prefix] || 999;
                        const orderB = prefixOrder[partsB.prefix] || 999;

                        if (orderA !== orderB) {
                            return orderA - orderB;
                        }

                        // If same prefix, sort by number
                        if (partsA.number !== partsB.number) {
                            return partsA.number - partsB.number;
                        }

                        // Fallback to string comparison
                        return poseIdA.localeCompare(poseIdB);
                    });
                    setPoses(sortedPoses);
                } else {
                    setError('Failed to load poses');
                }
            } catch (error) {
                console.error('Error fetching poses:', error);
                setError('Failed to load poses');
            } finally {
                setLoading(false);
            }
        };

        fetchPoses();
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handlePoseSelect = (pose: Pose) => {
        const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
        // Clean up the URL by removing extra quotes if they exist
        const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;

        // Toggle selection (checkbox behavior)
        setSelectedPoseIds(prev => {
            if (prev.includes(pose.poseId)) {
                // Deselect if already selected
                return prev.filter(id => id !== pose.poseId);
            } else {
                // Select if not selected
                return [...prev, pose.poseId];
            }
        });
        
        setIsDropdownOpen(false);
        if (onPoseSelect) {
            onPoseSelect(cleanImageUrl, pose.poseId);
        }
    };

    const getPoseDisplayName = (pose: Pose) => {
        if (pose.poseName && pose.poseName.trim()) {
            return pose.poseName;
        }
        return `${pose.type.charAt(0).toUpperCase() + pose.type.slice(1)} Pose`;
    };

    // Filter handling functions
    const handleGenderFilterChange = (gender: string) => {
        if (onGenderFilterChange) {
            onGenderFilterChange(gender);
        }
        // Remove selected poses that don't match the new filter
        setSelectedPoseIds(prev => {
            return prev.filter(poseId => {
                const pose = poses.find(p => p.poseId === poseId);
                return pose && pose.gender.toLowerCase() === gender.toLowerCase();
            });
        });
    };

    const handlePoseTypeFilterChange = (poseType: string) => {
        if (onPoseTypeFilterChange) {
            onPoseTypeFilterChange(poseType);
        }
        // Remove selected poses that don't match the new filter
        setSelectedPoseIds(prev => {
            return prev.filter(poseId => {
                const pose = poses.find(p => p.poseId === poseId);
                return pose && pose.type.toLowerCase() === poseType.toLowerCase();
            });
        });
    };

    const handleSizeFilterChange = (size: string) => {
        if (onSizeFilterChange) {
            onSizeFilterChange(size);
        }
    };

    // Filter poses based on gender, pose type, and size selection
    const filteredPoses = poses.filter(pose => {
        const matchesGender = pose.gender.toLowerCase() === genderFilter.toLowerCase();
        const matchesPoseType = pose.type.toLowerCase() === poseTypeFilter.toLowerCase();
        
        // Size filter logic based on size field
        let matchesSize = true;
        if (sizeFilter && pose.size) {
            matchesSize = pose.size.toLowerCase() === sizeFilter.toLowerCase();
        }
        
        return matchesGender && matchesPoseType && matchesSize && pose.liveStatus === true;
    });

    // Available filter options
    const genderOptions = ['male', 'female'];
    const sizeOptions = ['normal', 'plus'];
    // Pose type options are the same for both normal and plus (types are now consistent)
    const poseTypeOptions = ['standing', 'sitting', 'leaning', 'back'];

    if (loading) {
        return (
            <div className="p-2">
                <div className="flex items-center justify-center h-32">
                    <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-secondary-500"></div>
                        <span className="text-sm text-text-description">Loading poses...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-2">
                <div className="flex items-center justify-center h-32">
                    <div className="text-center">
                        <i className="fas fa-exclamation-triangle text-red-500 text-lg mb-2"></i>
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show collapsed view if poses are selected and component is collapsed (desktop only)
    if (isCollapsed && selectedPoseIds.length > 0) {
        const selectedPoses = poses.filter(p => selectedPoseIds.includes(p.poseId));

        return (
            <div className={`bg-accent-sand rounded-lg p-4 relative ${disabled ? 'opacity-50' : ''}`}>
                {/* Desktop collapsed view */}
                <div className="hidden md:block">
                    <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-3">
                            <span className="text-sm font-medium text-text-heading">Pose Selection</span>
                            {/* Gender Filter */}
                            <CustomSelect
                                value={genderFilter}
                                onChange={(value) => handleGenderFilterChange(value)}
                                options={genderOptions.map((gender) => ({
                                    value: gender,
                                    label: gender.charAt(0).toUpperCase() + gender.slice(1)
                                }))}
                                className="w-full"
                            />
                            {/* Size Filter */}
                            <CustomSelect
                                value={sizeFilter}
                                onChange={(value) => handleSizeFilterChange(value)}
                                options={sizeOptions.map((size) => ({
                                    value: size,
                                    label: size === 'normal' ? 'Normal' : 'Plus'
                                }))}
                                className="w-full"
                            />
                            {/* Pose Type Filter */}
                            <CustomSelect
                                value={poseTypeFilter}
                                onChange={(value) => handlePoseTypeFilterChange(value)}
                                options={poseTypeOptions.map((poseType) => ({
                                    value: poseType,
                                    label: poseType.charAt(0).toUpperCase() + poseType.slice(1)
                                }))}
                                className="w-full"
                            />
                        </div>
                        <button
                            onClick={() => setIsCollapsed(false)}
                            className="text-secondary-600 hover:text-secondary-800 text-sm font-medium"
                        >
                            Change
                        </button>
                    </div>

                    <div className="mt-3 space-y-2">
                        {selectedPoses.length > 0 ? (
                            <>
                                <div className="p-2 bg-secondary-100 rounded-lg">
                                    <p className="text-xs font-medium text-secondary-700">
                                        {selectedPoses.length} pose{selectedPoses.length !== 1 ? 's' : ''} selected
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-2">
                                    {selectedPoses.slice(0, 4).map((pose) => {
                                        const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                                        const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                                        return (
                                            <div key={pose.poseId} className="p-2 bg-white rounded-lg border border-accent-taupe">
                                                <img
                                                    src={cleanImageUrl}
                                                    alt={getPoseDisplayName(pose)}
                                                    className="w-full h-20 object-contain rounded-lg"
                                                    onError={(e) => {
                                                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                                                    }}
                                                />
                                                <p className="text-xs text-text-heading mt-1 truncate">{getPoseDisplayName(pose)}</p>
                                            </div>
                                        );
                                    })}
                                </div>
                                {selectedPoses.length > 4 && (
                                    <p className="text-xs text-text-description text-center">+{selectedPoses.length - 4} more</p>
                                )}
                            </>
                        ) : (
                            <div className="p-3 bg-white rounded-lg border border-accent-taupe">
                                <p className="text-sm text-text-description text-center">No poses selected</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile view - always show full component */}
                <div className="md:hidden">
                    {/* Mobile filters */}
                    <div className="flex items-center justify-between mb-4">
                        <span className="text-sm font-medium text-text-heading">Pose Selection</span>
                        <div className="flex space-x-2">
                            {/* Gender Filter */}
                            <CustomSelect
                                value={genderFilter}
                                onChange={(value) => handleGenderFilterChange(value)}
                                options={genderOptions.map((gender) => ({
                                    value: gender,
                                    label: gender.charAt(0).toUpperCase() + gender.slice(1)
                                }))}
                                className="w-full"
                            />
                            {/* Size Filter */}
                            <CustomSelect
                                value={sizeFilter}
                                onChange={(value) => handleSizeFilterChange(value)}
                                options={sizeOptions.map((size) => ({
                                    value: size,
                                    label: size === 'normal' ? 'Normal' : 'Plus'
                                }))}
                                className="w-full"
                            />
                            {/* Pose Type Filter */}
                            <CustomSelect
                                value={poseTypeFilter}
                                onChange={(value) => handlePoseTypeFilterChange(value)}
                                options={poseTypeOptions.map((poseType) => ({
                                    value: poseType,
                                    label: poseType.charAt(0).toUpperCase() + poseType.slice(1)
                                }))}
                                className="w-full"
                            />
                        </div>
                    </div>

                    {/* Mobile custom dropdown */}
                    <div className="relative mb-4" ref={dropdownRef}>
                        <button
                            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 bg-white text-left flex items-center justify-between"
                        >
                            <span className="truncate">
                                {selectedPoseIds.length > 0 
                                    ? `${selectedPoseIds.length} pose${selectedPoseIds.length !== 1 ? 's' : ''} selected`
                                    : 'Choose pose(s)...'}
                            </span>
                            <i className={`fas fa-chevron-${isDropdownOpen ? 'up' : 'down'} text-text-description ml-2`}></i>
                        </button>

                        {isDropdownOpen && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-accent-taupe rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                {filteredPoses.map((pose) => {
                                    const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                                    const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;

                                    return (
                                        <button
                                            key={pose.poseId}
                                            onClick={() => {
                                                const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                                                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;

                                                setSelectedPoseIds(prev => {
                                                    if (prev.includes(pose.poseId)) {
                                                        return prev.filter(id => id !== pose.poseId);
                                                    } else {
                                                        return [...prev, pose.poseId];
                                                    }
                                                });
                                                setIsDropdownOpen(false);
                                                if (onPoseSelect) {
                                                    onPoseSelect(cleanImageUrl, pose.poseId);
                                                }
                                            }}
                                            className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe last:border-b-0"
                                        >
                                            <img
                                                src={cleanImageUrl}
                                                alt={getPoseDisplayName(pose)}
                                                className="w-12 h-12 object-contain rounded-lg flex-shrink-0"
                                                onError={(e) => {
                                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                                                }}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-text-heading truncate">
                                                    {getPoseDisplayName(pose)}
                                                </p>
                                                <p className="text-xs text-text-description truncate">
                                                    {pose.type} - {pose.gender}
                                                </p>
                                            </div>
                                            {selectedPoseIds.includes(pose.poseId) && (
                                                <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Mobile pose preview */}
                    {selectedPoseIds.length > 0 && (
                        <div className="space-y-3">
                            <div className="p-2 bg-secondary-100 rounded-lg">
                                <p className="text-sm text-secondary-700">
                                    <i className="fas fa-info-circle mr-1"></i>
                                    {selectedPoseIds.length} pose{selectedPoseIds.length !== 1 ? 's' : ''} selected
                                </p>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {poses.filter(p => selectedPoseIds.includes(p.poseId)).slice(0, 4).map((pose) => {
                                    const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                                    const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                                    return (
                                        <div key={pose.poseId} className="relative bg-white rounded-lg overflow-hidden shadow-sm">
                                            <img
                                                src={cleanImageUrl}
                                                alt={getPoseDisplayName(pose)}
                                                className="w-full h-48 object-contain bg-accent-sand"
                                                onError={(e) => {
                                                    console.error('Image failed to load');
                                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400?text=Pose+Not+Available';
                                                }}
                                            />
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                                                <p className="text-xs font-medium text-white text-center truncate">
                                                    {getPoseDisplayName(pose)}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {selectedPoseIds.length > 4 && (
                                <p className="text-xs text-text-description text-center">+{selectedPoseIds.length - 4} more</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={`relative ${disabled ? 'opacity-50' : ''}`}>
            {disabled && (
                <div className="absolute inset-0 bg-accent-sand bg-opacity-90 rounded-lg flex items-center justify-center z-10">
                    <div className="text-center p-2">
                        <i className="fas fa-lock text-lg text-text-description mb-1"></i>
                        <p className="text-xs font-medium text-text-heading mb-1">Pose Selection Disabled</p>
                        <p className="text-xs text-text-description">Clear prompt to enable</p>
                    </div>
                </div>
            )}

            {/* Mobile custom dropdown - shown only on screens smaller than md */}
            <div className="md:hidden mb-4">
                <div className="relative mb-4" ref={dropdownRef}>
                    <button
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                        className="w-full border border-accent-taupe rounded-lg px-4 py-2 focus:ring-secondary-500 focus:border-secondary-500 bg-white text-left flex items-center justify-between"
                    >
                        <span className="truncate">
                            {selectedPoseIds.length > 0 
                                ? `${selectedPoseIds.length} pose${selectedPoseIds.length !== 1 ? 's' : ''} selected`
                                : 'Choose pose(s)...'}
                        </span>
                        <i className={`fas fa-chevron-${isDropdownOpen ? 'up' : 'down'} text-text-description ml-2`}></i>
                    </button>

                    {isDropdownOpen && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-accent-taupe rounded-lg shadow-lg max-h-64 overflow-y-auto">
                            {filteredPoses.map((pose) => {
                                const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;

                                return (
                                    <button
                                        key={pose.poseId}
                                        onClick={() => handlePoseSelect(pose)}
                                        className="w-full p-3 hover:bg-accent-sand flex items-center space-x-3 text-left border-b border-accent-taupe last:border-b-0"
                                    >
                                        <img
                                            src={cleanImageUrl}
                                            alt={getPoseDisplayName(pose)}
                                            className="w-12 h-12 object-contain rounded-lg flex-shrink-0"
                                            onError={(e) => {
                                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/48x48?text=N/A';
                                            }}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-text-heading truncate">
                                                {getPoseDisplayName(pose)}
                                            </p>
                                            <p className="text-xs text-text-description truncate">
                                                {pose.type} - {pose.gender}
                                            </p>
                                        </div>
                                        {selectedPoseIds.includes(pose.poseId) && (
                                            <i className="fas fa-check text-secondary-600 flex-shrink-0"></i>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>

                {/* Mobile pose preview */}
                {selectedPoseIds.length > 0 && (
                    <div className="space-y-3">
                        <div className="p-2 bg-secondary-100 rounded-lg">
                            <p className="text-sm text-secondary-700">
                                <i className="fas fa-info-circle mr-1"></i>
                                {selectedPoseIds.length} pose{selectedPoseIds.length !== 1 ? 's' : ''} selected
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {poses.filter(p => selectedPoseIds.includes(p.poseId)).slice(0, 4).map((pose) => {
                                const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                                const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                                return (
                                    <div key={pose.poseId} className="relative bg-white rounded-lg overflow-hidden shadow-sm">
                                        <img
                                            src={cleanImageUrl}
                                            alt={getPoseDisplayName(pose)}
                                            className="w-full h-48 object-contain bg-accent-sand"
                                            onError={(e) => {
                                                console.error('Image failed to load');
                                                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x400?text=Pose+Not+Available';
                                            }}
                                        />
                                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                                            <p className="text-xs font-medium text-white text-center truncate">
                                                {getPoseDisplayName(pose)}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {selectedPoseIds.length > 4 && (
                            <p className="text-xs text-text-description text-center">+{selectedPoseIds.length - 4} more</p>
                        )}
                    </div>
                )}
            </div>

            {/* Scrollable pose grid container */}
            <div className="max-h-80 overflow-y-auto">
                {/* Compact pose grid - inside scrollable container */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {filteredPoses.map((pose) => {
                        const imageUrl = Array.isArray(pose.imageUrl) ? pose.imageUrl[0] : pose.imageUrl;
                        const cleanImageUrl = typeof imageUrl === 'string' ? imageUrl.replace(/^"|"$/g, '') : imageUrl;
                        const isSelected = selectedPoseIds.includes(pose.poseId);

                        return (
                            <button
                                key={pose.poseId}
                                onClick={() => handlePoseSelect(pose)}
                                className={`group relative rounded-md overflow-hidden transition-all duration-200 ${isSelected
                                    ? 'ring-2 ring-secondary-500 shadow-md'
                                    : 'hover:shadow-sm hover:scale-105'
                                    }`}
                            >
                                                            <img
                                src={cleanImageUrl}
                                alt={getPoseDisplayName(pose)}
                                className="w-full h-40 object-contain bg-white"
                                onError={(e) => {
                                    console.error('Image failed to load:', cleanImageUrl);
                                    (e.target as HTMLImageElement).src = 'https://via.placeholder.com/120x144?text=N/A';
                                }}
                            />
                                <div className={`absolute inset-0 transition-opacity ${isSelected
                                    ? 'bg-secondary-500 bg-opacity-20'
                                    : 'bg-black bg-opacity-0 group-hover:bg-opacity-10'
                                    }`} />
                                {isSelected && (
                                    <div className="absolute top-1 right-1">
                                        <div className="bg-secondary-500 text-white rounded-full p-0.5">
                                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    </div>
                                )}
                                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-1">
                                    <p className="text-xs font-medium text-white text-center leading-tight truncate">
                                        {getPoseDisplayName(pose)}
                                    </p>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Compact selection info - outside scrollable area */}
            {selectedPoseIds.length > 0 && (
                <div className="mt-2 p-2 bg-secondary-100 rounded-md">
                    <p className="text-xs text-secondary-700">
                        <i className="fas fa-info-circle mr-1"></i>
                        {selectedPoseIds.length} pose{selectedPoseIds.length !== 1 ? 's' : ''} selected
                    </p>
                </div>
            )}
        </div>
    );
};

export default PoseSelection;
